import { useState, useEffect } from "react";
import { useLanguage } from "@/LanguageContext";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_senior-care-valais/artifacts/8pnba102_image.png";

const langs = [
  { code: "fr", label: "FR" },
  { code: "de", label: "DE" },
  { code: "en", label: "EN" },
];

export default function Header({ onLogoClick }) {
  const { t, language, setLanguage } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const navItems = [
    { id: "services", label: t("nav.services") },
    { id: "method", label: t("nav.method") },
    { id: "values", label: t("nav.values") },
    { id: "about", label: language === "de" ? "Über uns" : language === "en" ? "About" : "À propos" },
    { id: "testimonials", label: t("nav.testimonials") },
    { id: "contact", label: t("nav.contact") },
  ];

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#F0EBE3]/90 border-b border-[#E8E0D4]/30 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <button
            onClick={() => { onLogoClick?.(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            className="flex items-center gap-3 shrink-0"
            data-testid="header-logo"
          >
            <img
              src={LOGO_URL}
              alt="Présence & Sourire"
              className="h-20 w-20 rounded-full object-cover"
            />
            <span className="font-heading text-lg font-semibold text-[#2C3E50] hidden sm:block">
              Présence & Sourire
            </span>
          </button>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className="text-sm font-body text-[#2C3E50]/70 hover:text-[#6FA8B6] transition-colors tracking-wide"
                data-testid={`nav-${item.id}`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-4">
            {/* Language switcher */}
            <div className="flex items-center gap-1 bg-[#F3F4F6] rounded-full p-1" data-testid="language-switcher">
              {langs.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLanguage(l.code)}
                  className={`text-xs px-3 py-1.5 rounded-full font-body font-medium transition-all ${
                    language === l.code
                      ? "bg-[#6FA8B6] text-white"
                      : "text-[#2C3E50]/60 hover:text-[#2C3E50]"
                  }`}
                  data-testid={`lang-${l.code}`}
                >
                  {l.label}
                </button>
              ))}
            </div>

            {/* CTA desktop */}
            <button
              onClick={() => scrollTo("contact")}
              className="hidden lg:inline-flex bg-[#6FA8B6] text-white text-sm font-body font-medium px-5 py-2.5 rounded-full hover:bg-[#5B8D9A] transition-colors"
              data-testid="nav-cta-button"
            >
              {t("nav.cta")}
            </button>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 text-[#2C3E50]"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white/95 backdrop-blur-xl border-b border-[#E5E7EB]"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollTo(item.id)}
                  className="text-left text-base font-body text-[#2C3E50]/80 hover:text-[#6FA8B6] transition-colors py-2"
                  data-testid={`mobile-nav-${item.id}`}
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => scrollTo("contact")}
                className="mt-2 bg-[#6FA8B6] text-white font-body font-medium px-5 py-3 rounded-full hover:bg-[#5B8D9A] transition-colors text-center"
                data-testid="mobile-nav-cta"
              >
                {t("nav.cta")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
